/**
 * Service for Servicing Pipeline CRUD operations
 * Handles servicing_loans and servicing_payments tables
 * Tracks loans in interim servicing where Lendwise collects payments before investor transfer
 */

import { supabase } from '@/utils/supabase';
import { createComponentLogger } from '@/utils/logger';
import { getBusinessDateString, diffCalendarDays } from '@/utils/dateHelpers';
import {
  type ServicingLoan,
  type ServicingPayment,
  type CompletedServicingPayment,
  type ServicingSummaryMetrics,
  type CreateServicingLoanInput,
  type RecordPaymentInput,
  type ReverseServicingPaymentInput,
  type ServicingLoanStatus,
  type Form1098Data,
  type ServicerContact,
  type ServicingTransferNoticeData,
  type PaymentReminderLoan,
  type PaymentReminderResult,
  type PaymentReceivedPayload,
  type PaymentReceivedResult,
  toNumber,
  calculatePaymentCount,
  calculateInvestorWirePrincipalReduction,
  generatePaymentSchedule,
  generateSinglePayment,
  isSettledPaymentStatus,
  nextMonthlyDueDate,
  parseAmortizationMarkdown,
  validateScheduleAgainstDocument
} from '@/types/servicing';
import { resolveLoanProduct, selectTemplateForProduct } from '../../supabase/functions/_shared/investor-template-resolution';
import { reminderWindowCutoff } from '../../supabase/functions/_shared/servicing-reminder-window';

import { computeMonthlyAmounts } from '../../supabase/functions/_shared/servicing-math';
import { GOODBYE_PAYMENT_SELECT, paymentFieldsFromRow, resolveGoodbyePayment } from '../../supabase/functions/_shared/goodbye-letter-payment';
import { GoodbyeLetterPrereqError } from '@/types/goodbye-letter';

// Dynamic JSON-path projections cannot be inferred by PostgREST's select-string parser.
interface ServicingEncompassRow extends Record<string, unknown> {
  encompass_loan_id: string;
  first_payment_date: string | null;
  purchased_date?: string | null;
  funded_date?: string | null;
  closing_date?: string | null;
  borrower_first_name?: string | null;
  borrower_last_name?: string | null;
  co_borrower_first_name?: string | null;
  co_borrower_last_name?: string | null;
  borrower_mailing_street?: string | null;
  borrower_mailing_city?: string | null;
  borrower_mailing_state?: string | null;
  borrower_mailing_zip?: string | null;
  property_address?: string | null;
  subject_property_city?: string | null;
  subject_property_state?: string | null;
  property_zip?: string | null;
  investor_name?: string | null;
  rate_lock_investor_commitment?: string | null;
  rate_lock_loan_program?: string | null;
  trans_details_loan_type?: string | null;
  cx_doc_type?: string | null;
  trans_details_loan_amt?: number | string | null;
}

const log = createComponentLogger('ServicingService', 'database');

// Prerequisite errors carry copy written for users; anything else is logged and replaced.
function userFacingServicingError(error: unknown, fallback: string): string {
  if (error instanceof GoodbyeLetterPrereqError || (error instanceof Error && error.name === 'GoodbyeLetterPrereqError')) {
    return error.message;
  }
  log.warn('Servicing setup failed unexpectedly', {}, error as Error);
  return fallback;
}

let productAssignmentTableAvailable: boolean | null = null;

type InvestorTemplateResolution = {
  templateId: string | null;
  product: string | null;
  source: string;
  confidence: string;
  reason?: string;
};

function isMissingProductAssignmentTable(error: unknown): boolean {
  const err = error as { code?: string; message?: string; details?: string };
  const text = `${err.code ?? ''} ${err.message ?? ''} ${err.details ?? ''}`.toLowerCase();
  return (
    text.includes('investor_encompass_template_product_assignments') &&
    (text.includes('schema cache') || text.includes('could not find the table') || err.code === 'PGRST205')
  );
}

function resolveLegacyInvestorTemplate(
  templates: Array<{ id: string }>,
  productResolution: ReturnType<typeof resolveLoanProduct>
): InvestorTemplateResolution {
  if (templates.length === 1) {
    return {
      templateId: templates[0].id,
      product: productResolution.product,
      source: productResolution.source,
      confidence: productResolution.confidence,
      reason: 'legacy_single_template_fallback'
    };
  }

  return {
    templateId: null,
    product: productResolution.product,
    source: productResolution.source,
    confidence: productResolution.confidence,
    reason: templates.length === 0 ? 'no_template' : 'product_scope_unavailable_ambiguous_template'
  };
}

async function resolveInvestorTemplateForLoan(
  investorId: string,
  organizationId: string,
  loanFields: {
    rate_lock_loan_program?: unknown;
    trans_details_loan_type?: unknown;
    cx_doc_type?: unknown;
  }
): Promise<InvestorTemplateResolution> {
  const { data: templates, error: templateError } = await supabase
    .from('investor_encompass_templates')
    .select('id')
    .eq('investor_id', investorId)
    .eq('organization_id', organizationId);

  if (templateError) {
    throw templateError;
  }

  const productResolution = resolveLoanProduct({
    rateLockLoanProgram: loanFields.rate_lock_loan_program,
    transDetailsLoanType: loanFields.trans_details_loan_type,
    cxDocType: loanFields.cx_doc_type
  });

  const templateRows = (templates ?? []) as Array<{ id: string }>;
  let assignments: Array<{ template_id: string; product: string }> = [];

  if (productAssignmentTableAvailable === false) {
    return resolveLegacyInvestorTemplate(templateRows, productResolution);
  }

  const { data: assignmentRows, error: assignmentError } = await supabase
    .from('investor_encompass_template_product_assignments')
    .select('template_id, product')
    .eq('investor_id', investorId)
    .eq('organization_id', organizationId);

  if (assignmentError) {
    if (isMissingProductAssignmentTable(assignmentError)) {
      productAssignmentTableAvailable = false;
      log.warn(
        'Template product assignment table is unavailable; falling back to legacy investor template resolution',
        { investorId, organizationId },
        assignmentError
      );
      return resolveLegacyInvestorTemplate(templateRows, productResolution);
    }
    throw assignmentError;
  }

  productAssignmentTableAvailable = true;
  assignments = (assignmentRows ?? []) as Array<{ template_id: string; product: string }>;

  const selected = selectTemplateForProduct(
    templateRows,
    assignments,
    productResolution.product
  );

  return {
    templateId: selected.template?.id ?? null,
    product: productResolution.product,
    source: productResolution.source,
    confidence: productResolution.confidence,
    reason: selected.reason
  };
}

function mergeInterimServicingData(
  existing: unknown,
  investorTemplateResolution: Record<string, unknown> | null
): Record<string, unknown> | undefined {
  const base = existing && typeof existing === 'object' && !Array.isArray(existing)
    ? { ...(existing as Record<string, unknown>) }
    : {};
  if (investorTemplateResolution) {
    base.investor_template_resolution = investorTemplateResolution;
  }
  return Object.keys(base).length > 0 ? base : undefined;
}

// ============================================================================
// SERVICING LOAN CRUD
// ============================================================================

/**
 * List servicing loans for an organization, optionally filtered by status
 */
export async function listServicingLoans(
  organizationId: string,
  status?: ServicingLoanStatus
): Promise<ServicingLoan[]> {
  const timer = log.startTimer('listServicingLoans');

  let query = supabase
    .from('servicing_loans')
    .select('*')
    .eq('organization_id', organizationId)
    .order('first_payment_date', { ascending: true });

  if (status) {
    query = query.eq('status', status);
  }

  const { data, error } = await query;

  if (error) {
    log.error('Failed to list servicing loans', { organizationId, status }, error);
    log.endTimer(timer, { success: false });
    return [];
  }

  const loans = (data || []) as ServicingLoan[];
  await enrichWithLoanOfficerNames(loans);
  log.endTimer(timer, { success: true, count: loans.length });
  return loans;
}

/**
 * List received installments for loans whose full Lendwise servicing
 * obligation is complete. The result is one row per monthly payment, not one
 * row per loan.
 */
export async function listCompletedServicingPayments(
  organizationId: string
): Promise<CompletedServicingPayment[]> {
  const timer = log.startTimer('listCompletedServicingPayments');
  const { data, error } = await supabase
    .from('servicing_payments')
    .select('*, loan:servicing_loans!inner(*)')
    .eq('loan.organization_id', organizationId)
    .eq('loan.status', 'completed')
    .eq('status', 'received')
    .not('payment_received_date', 'is', null)
    .order('payment_received_date', { ascending: false });

  if (error) {
    log.error('Failed to list completed servicing payments', {
      metadata: { organizationId }
    }, error);
    log.endTimer(timer, { metadata: { success: false } });
    return [];
  }

  const rows = (data ?? []) as unknown as Array<ServicingPayment & { loan: ServicingLoan }>;
  const loansById = new Map<string, ServicingLoan>();
  for (const row of rows) {
    loansById.set(row.loan.id, row.loan);
  }
  await enrichWithLoanOfficerNames([...loansById.values()]);

  const results = rows.map((row) => {
    const { loan: _loan, ...payment } = row;
    return {
      payment: payment as ServicingPayment,
      loan: loansById.get(_loan.id) ?? _loan
    };
  });

  log.endTimer(timer, { metadata: { success: true, count: results.length } });
  return results;
}

/** Fetch all schedule, receipt, waiver, and reversal rows for a loan set. */
export async function getPaymentsForLoans(
  servicingLoanIds: string[]
): Promise<ServicingPayment[]> {
  if (servicingLoanIds.length === 0) {
    return [];
  }

  const { data, error } = await supabase
    .from('servicing_payments')
    .select('*')
    .in('servicing_loan_id', servicingLoanIds)
    .order('servicing_loan_id')
    .order('payment_number');

  if (error) {
    log.error('Failed to batch-load servicing payment history', {
      metadata: { loanCount: servicingLoanIds.length }
    }, error);
    throw error;
  }

  return (data ?? []) as ServicingPayment[];
}

export async function getCurrentUserEncompassUserId(): Promise<string | null> {
  const { data: authData, error: authError } = await supabase.auth.getUser();
  const userId = authData?.user?.id;
  if (authError || !userId) {
    log.warn('Unable to resolve current user for LO servicing access', { authError });
    return null;
  }

  const { data, error } = await supabase
    .from('users')
    .select('encompass_user_id')
    .eq('id', userId)
    .maybeSingle();

  if (error) {
    log.error('Failed to fetch current user Encompass ID', { userId }, error);
    return null;
  }

  return data?.encompass_user_id?.trim() || null;
}

export async function listOwnServicingLoans(
  status?: ServicingLoanStatus
): Promise<ServicingLoan[]> {
  const timer = log.startTimer('listOwnServicingLoans');

  const { data, error } = await supabase.rpc('list_own_servicing_loans', {
    p_status: status ?? null
  });

  if (error) {
    log.error('Failed to list own servicing loans', { status }, error);
    log.endTimer(timer, { success: false });
    return [];
  }

  const loans = Array.isArray(data) ? data as ServicingLoan[] : [];
  log.endTimer(timer, { success: true, count: loans.length });
  return loans;
}

export async function getOwnServicingLoan(id: string): Promise<ServicingLoan | null> {
  const timer = log.startTimer('getOwnServicingLoan');

  const { data, error } = await supabase.rpc('get_own_servicing_loan', {
    p_servicing_loan_id: id
  });

  if (error) {
    log.error('Failed to get own servicing loan', { id }, error);
    log.endTimer(timer, { success: false });
    return null;
  }

  const loan = data as ServicingLoan | null;
  log.endTimer(timer, { success: true, found: !!loan });
  return loan;
}

export async function getOwnPaymentsForLoan(servicingLoanId: string): Promise<ServicingPayment[]> {
  const timer = log.startTimer('getOwnPaymentsForLoan');

  const { data, error } = await supabase.rpc('list_own_servicing_payments', {
    p_servicing_loan_id: servicingLoanId
  });

  if (error) {
    log.error('Failed to get own servicing payments', { servicingLoanId }, error);
    log.endTimer(timer, { success: false });
    return [];
  }

  log.endTimer(timer, { success: true, count: Array.isArray(data) ? data.length : 0 });
  return Array.isArray(data) ? data as ServicingPayment[] : [];
}

export async function getOwnServicingSummaryMetrics(
  loans: ServicingLoan[]
): Promise<ServicingSummaryMetrics> {
  if (loans.length === 0) {
    return {
      totalActiveLoans: 0,
      totalExpectedPayments: 0,
      totalCollected: 0,
      totalOutstanding: 0,
      netServicingIncome: 0,
      overduePaymentsCount: 0
    };
  }

  const totalExpected = loans.reduce((sum, loan) => sum + toNumber(loan.total_expected_payments), 0);
  const totalCollected = loans.reduce((sum, loan) => sum + toNumber(loan.total_received_payments), 0);
  const overdueCount = loans.filter((loan) => loan.has_overdue_payments).length;

  return {
    totalActiveLoans: loans.filter((loan) => loan.status === 'active').length,
    totalExpectedPayments: Math.round(totalExpected * 100) / 100,
    totalCollected: Math.round(totalCollected * 100) / 100,
    totalOutstanding: Math.round((totalExpected - totalCollected) * 100) / 100,
    netServicingIncome: 0,
    overduePaymentsCount: overdueCount
  };
}

/**
 * Batch-resolve loan officer names from encompass_loans.
 * Builds a combined "LO / LO2" display string and sets loan_officer_display.
 */
async function enrichWithLoanOfficerNames(loans: ServicingLoan[]): Promise<void> {
  if (loans.length === 0) {
    return;
  }

  const encompassIds = [...new Set(loans.map(l => l.encompass_loan_id).filter(Boolean))];
  if (encompassIds.length === 0) {
    return;
  }

  const { data, error } = await supabase
    .from('encompass_loans')
    .select('encompass_loan_id, loan_officer_first_name, loan_officer_last_name, loan_officer_2_first_name, loan_officer_2_last_name')
    .in('encompass_loan_id', encompassIds);

  if (error || !data) {
    log.warn('Failed to resolve loan officer names', { error });
    return;
  }

  const loMap = new Map(
    data.map(row => [row.encompass_loan_id, buildLoanOfficerDisplay(row)])
  );

  for (const loan of loans) {
    loan.loan_officer_display = loMap.get(loan.encompass_loan_id) ?? null;
  }
}

/** Build combined "FirstName LastName / FirstName2 LastName2" display string. */
function buildLoanOfficerDisplay(row: Record<string, unknown>): string | null {
  const lo = [row.loan_officer_first_name, row.loan_officer_last_name]
    .filter(Boolean)
    .join(' ')
    .trim();
  const lo2 = [row.loan_officer_2_first_name, row.loan_officer_2_last_name]
    .filter(Boolean)
    .join(' ')
    .trim();

  if (lo && lo2) {
    return `${lo} / ${lo2}`;
  }
  if (lo) {
    return lo;
  }
  if (lo2) {
    return lo2;
  }
  return null;
}

/**
 * Get a single servicing loan by ID
 */
export async function getServicingLoan(id: string): Promise<ServicingLoan | null> {
  const timer = log.startTimer('getServicingLoan');

  const { data, error } = await supabase
    .from('servicing_loans')
    .select('*')
    .eq('id', id)
    .maybeSingle();

  if (error) {
    log.error('Failed to get servicing loan', { id }, error);
    log.endTimer(timer, { success: false });
    return null;
  }

  log.endTimer(timer, { success: true, found: !!data });
  return data as ServicingLoan | null;
}

/**
 * Get servicing loan by encompass loan ID for an organization
 */
export async function getServicingLoanByEncompassId(
  organizationId: string,
  encompassLoanId: string
): Promise<ServicingLoan | null> {
  const timer = log.startTimer('getServicingLoanByEncompassId');

  const { data, error } = await supabase
    .from('servicing_loans')
    .select('*')
    .eq('organization_id', organizationId)
    .eq('encompass_loan_id', encompassLoanId)
    .maybeSingle();

  if (error) {
    log.error('Failed to get servicing loan by encompass ID', { organizationId, encompassLoanId }, error);
    log.endTimer(timer, { success: false });
    return null;
  }

  log.endTimer(timer, { success: true, found: !!data });
  return data as ServicingLoan | null;
}

/**
 * Create a servicing loan record
 */
export async function createServicingLoan(
  input: CreateServicingLoanInput
): Promise<ServicingLoan | null> {
  const timer = log.startTimer('createServicingLoan');

  const { data, error } = await supabase
    .from('servicing_loans')
    .insert(input)
    .select()
    .single();

  if (error) {
    log.error('Failed to create servicing loan', { loanNumber: input.loan_number }, error);
    log.endTimer(timer, { success: false });
    return null;
  }

  log.info('Created servicing loan', { id: data.id, loanNumber: input.loan_number });
  log.endTimer(timer, { success: true });
  return data as ServicingLoan;
}

/**
 * Update a servicing loan record
 */
export async function updateServicingLoan(
  id: string,
  updates: Partial<ServicingLoan>,
  userId?: string
): Promise<ServicingLoan | null> {
  const timer = log.startTimer('updateServicingLoan');

  const updateData = {
    ...updates,
    updated_by: userId || updates.updated_by
  };
  // Remove read-only fields
  delete updateData.id;
  delete updateData.created_at;

  const { data, error } = await supabase
    .from('servicing_loans')
    .update(updateData)
    .eq('id', id)
    .select()
    .single();

  if (error) {
    log.error('Failed to update servicing loan', { id }, error);
    log.endTimer(timer, { success: false });
    return null;
  }

  log.endTimer(timer, { success: true });
  return data as ServicingLoan;
}

// ============================================================================
// PAYMENT CRUD
// ============================================================================

/**
 * Get all payments for a servicing loan, ordered by payment number
 */
export async function getPaymentsForLoan(
  servicingLoanId: string
): Promise<ServicingPayment[]> {
  const timer = log.startTimer('getPaymentsForLoan');

  const { data, error } = await supabase
    .from('servicing_payments')
    .select('*')
    .eq('servicing_loan_id', servicingLoanId)
    .order('payment_number', { ascending: true });

  if (error) {
    log.error('Failed to get payments', { servicingLoanId }, error);
    log.endTimer(timer, { success: false });
    return [];
  }

  log.endTimer(timer, { success: true, count: data?.length || 0 });
  return (data || []) as ServicingPayment[];
}

/**
 * Record a payment received. Updates payment row and recalculates loan totals.
 */
export async function recordPayment(
  input: RecordPaymentInput
): Promise<{ success: boolean; error?: string; nextPaymentDueDate?: string; nextPaymentAmount?: number }> {
  const timer = log.startTimer('recordPayment');

  // Validate amount
  if (toNumber(input.total_amount_received) <= 0) {
    log.endTimer(timer, { success: false });
    return { success: false, error: 'Payment amount must be greater than zero' };
  }

  // Find the payment row (include scheduled amounts for validation)
  const { data: existing, error: findError } = await supabase
    .from('servicing_payments')
    .select('id, status, total_amount_due, principal_due, interest_due, escrow_due')
    .eq('servicing_loan_id', input.servicing_loan_id)
    .eq('payment_number', input.payment_number)
    .maybeSingle();

  if (findError || !existing) {
    log.error('Payment not found', { servicingLoanId: input.servicing_loan_id, paymentNumber: input.payment_number }, findError);
    log.endTimer(timer, { success: false });
    return { success: false, error: 'Payment schedule entry not found' };
  }

  // Guard: reject if payment is already received or waived
  if (existing.status === 'received' || existing.status === 'waived') {
    log.warn('Payment not recordable', { paymentId: existing.id, status: existing.status, paymentNumber: input.payment_number });
    log.endTimer(timer, { success: false });
    return { success: false, error: `Payment #${input.payment_number} cannot be recorded (status: ${existing.status}).` };
  }

  // Guard: verify all amounts match scheduled values (defense-in-depth)
  const checks = [
    { field: 'total', received: toNumber(input.total_amount_received), due: toNumber(existing.total_amount_due) },
    { field: 'principal', received: toNumber(input.principal_received), due: toNumber(existing.principal_due) },
    { field: 'interest', received: toNumber(input.interest_received), due: toNumber(existing.interest_due) },
    { field: 'escrow', received: toNumber(input.escrow_received), due: toNumber(existing.escrow_due) }
  ];
  for (const c of checks) {
    if (Math.abs(c.received - c.due) > 0.01) {
      log.warn('Payment amount mismatch', { paymentNumber: input.payment_number, field: c.field, received: c.received, due: c.due });
      log.endTimer(timer, { success: false });
      return { success: false, error: `${c.field} received ($${c.received.toFixed(2)}) does not match scheduled amount ($${c.due.toFixed(2)}).` };
    }
  }

  // Update payment record
  const { error: updateError } = await supabase
    .from('servicing_payments')
    .update({
      payment_received_date: input.payment_received_date,
      principal_received: toNumber(input.principal_received),
      interest_received: toNumber(input.interest_received),
      escrow_received: toNumber(input.escrow_received),
      total_amount_received: toNumber(input.total_amount_received),
      late_fee: 0, // Late fees deferred — always 0 in this PR
      payment_method: input.payment_method || null,
      reference_number: input.reference_number || null,
      notes: input.notes || null,
      recorded_by: input.recorded_by || null,
      status: 'received'
    })
    .eq('id', existing.id);

  if (updateError) {
    log.error('Failed to update payment', { paymentId: existing.id }, updateError);
    log.endTimer(timer, { success: false });
    return { success: false, error: 'Failed to record payment' };
  }

  // Recalculate loan totals from all payment rows (may append next payment for pre-recon loans)
  const recalcResult = await recalculateLoanTotals(input.servicing_loan_id);

  log.info('Payment recorded', {
    servicingLoanId: input.servicing_loan_id,
    paymentNumber: input.payment_number,
    amount: input.total_amount_received
  });
  log.endTimer(timer, { success: true });
  return {
    success: true,
    nextPaymentDueDate: recalcResult.appendedDueDate,
    nextPaymentAmount: recalcResult.appendedAmount
  };
}

/**
 * Mark a previously recorded payment as bounced/reversed and reopen the loan.
 * One reversal cycle is supported per payment row.
 */
export async function reversePayment(
  input: ReverseServicingPaymentInput
): Promise<{ success: boolean; error?: string }> {
  const timer = log.startTimer('reversePayment');

  const { data: existing, error: findError } = await supabase
    .from('servicing_payments')
    .select(`
      id,
      status,
      payment_received_date,
      principal_received,
      interest_received,
      escrow_received,
      total_amount_received,
      payment_method,
      reference_number,
      reversed_at
    `)
    .eq('servicing_loan_id', input.servicing_loan_id)
    .eq('payment_number', input.payment_number)
    .maybeSingle();

  if (findError || !existing) {
    log.error('Payment not found for reversal', {
      servicingLoanId: input.servicing_loan_id,
      paymentNumber: input.payment_number
    }, findError);
    log.endTimer(timer, { success: false });
    return { success: false, error: 'Payment schedule entry not found' };
  }

  if (existing.status !== 'received') {
    log.warn('Payment not reversible', {
      paymentId: existing.id,
      paymentNumber: input.payment_number,
      status: existing.status
    });
    log.endTimer(timer, { success: false });
    return { success: false, error: `Payment #${input.payment_number} cannot be reversed (status: ${existing.status}).` };
  }

  if (existing.reversed_at) {
    log.warn('Payment already has reversal history', {
      paymentId: existing.id,
      paymentNumber: input.payment_number
    });
    log.endTimer(timer, { success: false });
    return { success: false, error: `Payment #${input.payment_number} already has a prior reversal. Multiple bounce cycles are not supported.` };
  }

  const { error: updateError } = await supabase
    .from('servicing_payments')
    .update({
      reversed_at: input.reversed_at,
      reversal_reason: input.reversal_reason,
      reversal_notes: input.reversal_notes || null,
      reversed_by: input.reversed_by || null,
      reversed_payment_received_date: existing.payment_received_date,
      reversed_principal_received: toNumber(existing.principal_received),
      reversed_interest_received: toNumber(existing.interest_received),
      reversed_escrow_received: toNumber(existing.escrow_received),
      reversed_total_amount_received: toNumber(existing.total_amount_received),
      reversed_payment_method: existing.payment_method || null,
      reversed_reference_number: existing.reference_number || null,
      payment_received_date: null,
      principal_received: 0,
      interest_received: 0,
      escrow_received: 0,
      total_amount_received: 0,
      payment_method: null,
      reference_number: null,
      status: 'bounced'
    })
    .eq('id', existing.id);

  if (updateError) {
    log.error('Failed to reverse payment', { paymentId: existing.id }, updateError);
    log.endTimer(timer, { success: false });
    return { success: false, error: 'Failed to mark payment as bounced' };
  }

  await recalculateLoanTotals(input.servicing_loan_id);

  log.info('Payment reversed', {
    servicingLoanId: input.servicing_loan_id,
    paymentNumber: input.payment_number,
    reason: input.reversal_reason
  });
  log.endTimer(timer, { success: true });
  return { success: true };
}

/**
 * Recalculate servicing loan totals from payment rows.
 *
 * When all payments are settled on a pre-recon unpurchased loan, appends the
 * next monthly payment so the loan stays active until the investor buys.
 * Returns appended payment info when a roll-forward occurs.
 */
async function recalculateLoanTotals(
  servicingLoanId: string
): Promise<{ appendedDueDate?: string; appendedAmount?: number }> {
  let payments = await getPaymentsForLoan(servicingLoanId);

  // Fetch loan header for pre-recon roll-forward decision
  const { data: loanRow } = await supabase
    .from('servicing_loans')
    .select('banker_recon_id, encompass_loan_id, first_payment_date, loan_amount, interest_rate, loan_term_months, status')
    .eq('id', servicingLoanId)
    .single();

  let allSettled = payments.length > 0 && payments.every(
    (p) => isSettledPaymentStatus(p.status)
  );

  // Pre-recon roll-forward: if all payments settled and investor hasn't purchased,
  // append the next monthly payment to keep the loan active.
  let appendedDueDate: string | undefined;
  let appendedAmount: number | undefined;

  if (allSettled && loanRow?.status === 'active' && loanRow.banker_recon_id === null) {
    try {
      const { data: rawEl, error: elError } = await supabase
        .from('encompass_loans')
        .select(`purchased_date, first_payment_date, rate_lock_base_loan_amount, trans_details_interest_rate, loan_term_months, ${GOODBYE_PAYMENT_SELECT}`)
        .eq('encompass_loan_id', loanRow.encompass_loan_id)
        .maybeSingle();

      const el = rawEl as unknown as ServicingEncompassRow | null;
      if (elError || !el) {
        throw new GoodbyeLetterPrereqError('PAYMENT_DATA_INCOMPLETE', 'Cannot verify the next payment in Encompass. Review required.');
      }
      if (el.purchased_date === null) {
        const lastPayment = payments[payments.length - 1];
        if (lastPayment) {
          const nextDueDate = nextMonthlyDueDate(lastPayment.due_date);
          const amounts = computeMonthlyAmounts(el as Record<string, unknown>, loanRow.first_payment_date, nextDueDate);
          const totalPrincipalDue = payments.reduce(
            (sum, p) => sum + toNumber(p.principal_due), 0
          );
          const remainingBalance = amounts.loanAmount - totalPrincipalDue;
          const nextPaymentNumber = lastPayment.payment_number + 1;

          const entry = generateSinglePayment(
            remainingBalance, amounts.interestRate,
            amounts.monthlyPI, amounts.monthlyEscrow,
            nextDueDate, nextPaymentNumber
          );

          const { error: insertError } = await supabase
            .from('servicing_payments')
            .insert({
              servicing_loan_id: servicingLoanId,
              payment_number: entry.payment_number,
              due_date: entry.due_date,
              principal_due: entry.principal_due,
              interest_due: entry.interest_due,
              escrow_due: entry.escrow_due,
              total_amount_due: entry.total_amount_due,
              interest_rate: entry.interest_rate,
              status: 'scheduled',
              source: 'manual'
            });

          if (insertError) {
            log.error('Failed to append next payment', { servicingLoanId, nextPaymentNumber }, insertError);
            // Conflict may mean the row already exists (sprout or retry).
            // Re-read payments so aggregates reflect actual state and allSettled
            // is recalculated — prevents false auto-completion.
            payments = await getPaymentsForLoan(servicingLoanId);
            allSettled = payments.length > 0 && payments.every(
              (p) => isSettledPaymentStatus(p.status)
            );
          } else {
            appendedDueDate = entry.due_date;
            appendedAmount = entry.total_amount_due;
            // Re-fetch payments so the aggregate pass below includes the new row
            payments = await getPaymentsForLoan(servicingLoanId);
            allSettled = false; // New scheduled row prevents auto-completion
            log.info('Appended next payment (pre-recon unpurchased)', {
              servicingLoanId, paymentNumber: nextPaymentNumber, dueDate: nextDueDate
            });
          }
        }
      }
    } catch (error) {
      // A receipt remains valid even when the next scheduled amount cannot be verified.
      allSettled = false;
      const message = error instanceof Error ? error.message : 'Next payment needs review.';
      await supabase.from('servicing_loans').update({ sync_error: `PAYMENT_REVIEW: ${message}` }).eq('id', servicingLoanId);
      log.warn('Next payment blocked for review', { metadata: { servicingLoanId, message } });
    }
  }

  // Aggregate calculation from all payments (including any appended row)
  let totalExpected = 0;
  let totalReceived = 0;
  let totalPrincipal = 0;
  let totalInterest = 0;
  let totalEscrow = 0;

  for (const p of payments) {
    totalExpected += toNumber(p.total_amount_due);
    totalReceived += toNumber(p.total_amount_received);
    totalPrincipal += toNumber(p.principal_received);
    totalInterest += toNumber(p.interest_received);
    totalEscrow += toNumber(p.escrow_received);
  }

  // Net servicing income = interest only (principal reduces balance, escrow is pass-through)
  const netIncome = totalInterest;

  // Compute status fields for ServicingTable display
  const today = new Date().toISOString().split('T')[0];
  const nextDuePayment = payments.find((p) => !isSettledPaymentStatus(p.status));
  const receivedCount = payments.filter(p => p.status === 'received').length;
  const hasOverdue = payments.some(
    p => !isSettledPaymentStatus(p.status) && p.due_date < today
  );

  await supabase
    .from('servicing_loans')
    .update({
      payment_count: payments.length,
      total_expected_payments: Math.round(totalExpected * 100) / 100,
      total_received_payments: Math.round(totalReceived * 100) / 100,
      total_principal_collected: Math.round(totalPrincipal * 100) / 100,
      total_interest_collected: Math.round(totalInterest * 100) / 100,
      total_escrow_collected: Math.round(totalEscrow * 100) / 100,
      net_servicing_income: Math.round(netIncome * 100) / 100,
      next_payment_due_date: nextDuePayment?.due_date || null,
      received_payment_count: receivedCount,
      has_overdue_payments: hasOverdue
    })
    .eq('id', servicingLoanId);

  if (!allSettled && loanRow?.status === 'completed') {
    await supabase
      .from('servicing_loans')
      .update({ status: 'active' })
      .eq('id', servicingLoanId)
      .eq('status', 'completed');

    log.info('Servicing loan reopened after reversal/unsettled payment', { servicingLoanId });
  }

  // Auto-complete: only fires when append did NOT occur (allSettled still true)
  if (allSettled) {
    // If any payment was actually received → completed (Lendwise collected)
    // If ALL were waived (none received) → transferred (investor took over)
    const anyReceived = payments.some((p) => p.status === 'received');
    const newStatus = anyReceived ? 'completed' : 'transferred';

    await supabase
      .from('servicing_loans')
      .update({ status: newStatus })
      .eq('id', servicingLoanId)
      .eq('status', 'active');

    log.info(`Servicing loan auto-${newStatus}`, { servicingLoanId });
  }

  // Note: Banker recon values are locked at finalization (expected from amortization schedule).
  // Servicing pipeline tracks actuals independently — no cross-update to banker_recons here.
  return { appendedDueDate, appendedAmount };
}

// ============================================================================
// UPCOMING SERVICING LOANS (Virtual rows from encompass_loans)
// ============================================================================

/**
 * Query encompass_loans for funded Banked-Retail loans that are NOT yet
 * purchased and NOT already in servicing_loans. Returns them projected into
 * the ServicingLoan shape as virtual rows.
 *
 * Sprouting creates the real record at funding (webhook) or via the daily
 * sweep, so these virtual rows are a same-day fallback: one still visible
 * after a refresh means record activation failed and needs investigation.
 */
export async function listUpcomingServicingLoans(
  organizationId: string
): Promise<ServicingLoan[]> {
  const timer = log.startTimer('listUpcomingServicingLoans');

  // 1. Get encompass_loan_ids already in servicing_loans for this org
  const { data: existing } = await supabase
    .from('servicing_loans')
    .select('encompass_loan_id')
    .eq('organization_id', organizationId);

  const existingIds = new Set((existing || []).map(r => r.encompass_loan_id));

  // 2. Query encompass_loans for candidates
  const { data: candidates, error } = await supabase
    .from('encompass_loans')
    .select(
      `encompass_loan_id, organization_id, trans_details_loan_number, ` +
      `borrower_first_name, borrower_last_name, ` +
      `rate_lock_base_loan_amount, trans_details_interest_rate, ` +
      `loan_term_months, investor_name, ` +
      `closing_date, funded_date, first_payment_date, ${GOODBYE_PAYMENT_SELECT}, fees_prepaid_interest, ` +
      `loan_officer_first_name, loan_officer_last_name, ` +
      `loan_officer_2_first_name, loan_officer_2_last_name`
    )
    .eq('organization_id', organizationId)
    .eq('channel', 'Banked - Retail')
    .is('purchased_date', null)
    .not('funded_date', 'is', null)
    .not('first_payment_date', 'is', null)
    .order('first_payment_date', { ascending: true });

  if (error) {
    log.error('Failed to list upcoming servicing loans', { organizationId }, error);
    log.endTimer(timer, { success: false });
    return [];
  }

  // 3. Filter out loans already in servicing_loans
  const upcoming = (candidates || [])
    .filter(el => !existingIds.has(el.encompass_loan_id))
    .map(el => projectToServicingLoan(el, organizationId));

  log.endTimer(timer, { success: true, count: upcoming.length });
  return upcoming;
}

/**
 * Project an encompass_loans row into the ServicingLoan shape.
 * Computes monthly payment using the shared escrow helper.
 */
function projectToServicingLoan(
  el: Record<string, unknown>,
  organizationId: string
): ServicingLoan {
  let totalMonthly: number | null = null;
  let paymentReview: string | null = null;
  try {
    totalMonthly = computeMonthlyAmounts(el, el.first_payment_date as string).paymentEvidence.amountCents / 100;
  } catch (error) {
    paymentReview = error instanceof Error ? error.message : 'Payment needs review in Encompass.';
  }
  const loanAmount = toNumber(el.rate_lock_base_loan_amount);
  const interestRate = toNumber(el.trans_details_interest_rate);
  const termMonths = el.loan_term_months as number | null;

  return {
    id: `upcoming-${el.encompass_loan_id}`,
    organization_id: organizationId,
    encompass_loan_id: el.encompass_loan_id as string,
    banker_recon_id: null,
    loan_number: (el.trans_details_loan_number as string) || '',
    borrower_name: `${el.borrower_first_name || ''} ${el.borrower_last_name || ''}`.trim() || null,
    loan_officer_display: buildLoanOfficerDisplay(el),
    loan_amount: loanAmount,
    interest_rate: interestRate,
    loan_term_months: termMonths,
    closing_date: (el.closing_date as string) || null,
    funded_date: (el.funded_date as string) || null,
    first_payment_date: el.first_payment_date as string,
    investor_purchase_date: null,
    first_payment_to_investor: null,
    servicing_transfer_date: null,
    investor_name: (el.investor_name as string) || null,
    prepaid_interest: toNumber(el.fees_prepaid_interest),
    status: 'active',
    interim_servicing_data: null,
    last_sync_at: null,
    sync_error: paymentReview ? `PAYMENT_REVIEW: ${paymentReview}` : null,
    payment_count: 1,
    total_expected_payments: totalMonthly,
    total_received_payments: 0,
    total_principal_collected: 0,
    total_interest_collected: 0,
    total_escrow_collected: 0,
    net_servicing_income: 0,
    investor_template_id: null,
    next_payment_due_date: (el.first_payment_date as string) || null,
    received_payment_count: 0,
    has_overdue_payments: false,
    created_by: null,
    updated_by: null,
    created_at: '',
    updated_at: '',
    _source: 'encompass_loans'
  };
}

// ============================================================================
// CREATE FROM BANKER RECON (Idempotent)
// ============================================================================

/**
 * Auto-create a servicing loan from a finalized banker recon.
 * Uses upsert on (organization_id, encompass_loan_id) to be idempotent.
 */
export async function createFromBankerRecon(
  bankerReconId: string
): Promise<{ success: boolean; servicingLoanId?: string; error?: string }> {
  const timer = log.startTimer('createFromBankerRecon');

  // 1. Read banker recon
  const { data: recon, error: reconError } = await supabase
    .from('banker_recons')
    .select('*')
    .eq('id', bankerReconId)
    .single();

  if (reconError || !recon) {
    log.error('Banker recon not found', { bankerReconId }, reconError);
    log.endTimer(timer, { success: false });
    return { success: false, error: 'Banker recon not found' };
  }

  // 2. Read encompass loan for rate, P&I, total payment, and first_payment_date
  const { data: rawEncompassLoan } = await supabase
    .from('encompass_loans')
    .select(`trans_details_interest_rate, loan_term_months, interest_only_term_months, investor_name, funded_date, closing_date, first_payment_date, borrower_first_name, borrower_last_name, rate_lock_base_loan_amount, trans_details_loan_amt, fees_prepaid_interest, rate_lock_loan_program, trans_details_loan_type, cx_doc_type, ${GOODBYE_PAYMENT_SELECT}`)
    .eq('encompass_loan_id', recon.encompass_loan_id)
    .maybeSingle();

  const encompassLoan = rawEncompassLoan as unknown as ServicingEncompassRow | null;

  // 3. Check if servicing is applicable
  const firstPaymentDate = encompassLoan?.first_payment_date;
  const firstPaymentToInvestor = recon.first_payment_to_investor;

  if (!firstPaymentDate || !firstPaymentToInvestor) {
    log.info('Servicing not applicable: missing payment dates', {
      bankerReconId,
      firstPaymentDate,
      firstPaymentToInvestor
    });
    log.endTimer(timer, { success: false });
    return { success: false, error: 'Missing first payment date or first payment to investor date' };
  }

  const paymentCount = calculatePaymentCount(firstPaymentDate, firstPaymentToInvestor);
  // Zero-payment loans (investor purchased before first payment due) still get a
  // servicing record with status "transferred" — they show prepaid interest at closing.
  const servicingStatus = paymentCount > 0 ? 'active' : 'transferred';

  // Use recon.loan_amount for the header; encompass fields for PI/escrow calculation
  const loanAmount = toNumber(recon.loan_amount);
  const borrowerName = encompassLoan
    ? `${encompassLoan.borrower_first_name || ''} ${encompassLoan.borrower_last_name || ''}`.trim()
    : recon.loan_snapshot?.borrowerName || recon.loan_snapshot?.borrower_first_name
      ? `${recon.loan_snapshot.borrower_first_name} ${recon.loan_snapshot.borrower_last_name}`.trim()
      : null;

  // Validate every generated period, including the investor's first installment, before any write.
  let computed: ReturnType<typeof computeMonthlyAmounts>;
  try {
    computed = computeMonthlyAmounts(encompassLoan as Record<string, unknown>, firstPaymentDate, firstPaymentToInvestor);
    // The recon stores the total note amount (trans_details_loan_amt, which includes any financed
    // FHA UFMIP or VA funding fee). The rate-lock base excludes that premium, so compare totals.
    const encompassNoteAmount = toNumber(encompassLoan?.trans_details_loan_amt, Number.NaN);
    if (!Number.isFinite(encompassNoteAmount) || encompassNoteAmount <= 0) {
      throw new GoodbyeLetterPrereqError('PAYMENT_DATA_INCOMPLETE', 'The Encompass loan amount is missing. Synchronize the loan and retry.');
    }
    if (Math.round(loanAmount * 100) !== Math.round(encompassNoteAmount * 100)) {
      throw new GoodbyeLetterPrereqError('PAYMENT_DATA_CONFLICT', 'Recon and Encompass loan balances disagree. Review before creating the schedule.');
    }
    resolveGoodbyePayment(paymentFieldsFromRow(encompassLoan as Record<string, unknown>), firstPaymentDate, firstPaymentDate);
  } catch (error) {
    log.endTimer(timer, { metadata: { success: false } });
    return { success: false, error: userFacingServicingError(error, 'Payment needs review.') };
  }
  const { interestRate, termMonths, monthlyPI, monthlyEscrow } = computed;
  const interestOnlyTermMonths = toNumber(encompassLoan?.interest_only_term_months);

  // 4b. Resolve purchase investor display + template for servicer contact lookup.
  // The finalized banker recon's investor_id is the purchase source of truth;
  // the Encompass loan row can still carry a stale lock investor.
  let purchaseInvestorName: string | null = null;
  let investorTemplateId: string | null = null;
  let investorTemplateResolution: Record<string, unknown> | null = null;
  if (recon.investor_id) {
    const { data: investor } = await supabase
      .from('investors')
      .select('investor_name, servicer_selection_mode')
      .eq('id', recon.investor_id)
      .maybeSingle();
    purchaseInvestorName = investor?.investor_name?.trim() || null;

    const resolved = investor?.servicer_selection_mode === 'per_loan'
      ? { ...resolveLoanProduct({ rateLockLoanProgram: encompassLoan?.rate_lock_loan_program, transDetailsLoanType: encompassLoan?.trans_details_loan_type, cxDocType: encompassLoan?.cx_doc_type }), templateId: recon.servicing_template_id ?? null, reason: 'per_loan_selection' }
      : await resolveInvestorTemplateForLoan(
      recon.investor_id,
      recon.organization_id,
      encompassLoan ?? {}
    );
    investorTemplateId = resolved.templateId;
    investorTemplateResolution = {
      product: resolved.product,
      source: resolved.source,
      confidence: resolved.confidence,
      reason: resolved.reason,
      resolved_at: new Date().toISOString()
    };
  }
  purchaseInvestorName = purchaseInvestorName
    || encompassLoan?.investor_name
    || recon.loan_snapshot?.investor_name
    || null;

  // 4c. Guard: if the servicing loan already has operational payment history
  // (any payment not in 'scheduled' state), preserve history but reconcile the schedule.
  const { data: existingLoan, error: existingLoanError } = await supabase
    .from('servicing_loans')
    .select('id, interim_servicing_data, sync_error')
    .eq('organization_id', recon.organization_id)
    .eq('encompass_loan_id', recon.encompass_loan_id)
    .maybeSingle();

  if (existingLoanError) {
    log.endTimer(timer, { metadata: { success: false } });
    return { success: false, error: 'Existing servicing history could not be verified. Retry before creating a schedule.' };
  }

  if (existingLoan) {
    const { count: nonScheduledCount, error: historyError } = await supabase
      .from('servicing_payments')
      .select('id', { count: 'exact', head: true })
      .eq('servicing_loan_id', existingLoan.id)
      .neq('status', 'scheduled');

    if (historyError || nonScheduledCount === null || nonScheduledCount === undefined) {
      log.endTimer(timer, { metadata: { success: false } });
      return { success: false, error: 'Payment history could not be verified. No schedule was changed.' };
    }

    if (nonScheduledCount && nonScheduledCount > 0) {
      try {
        const retainedPayments = await getPaymentsForLoan(existingLoan.id);
        for (const payment of retainedPayments) {
          const willBeTrimmed = payment.status === 'scheduled' && payment.due_date >= firstPaymentToInvestor;
          if (!isSettledPaymentStatus(payment.status) && !willBeTrimmed) {
            resolveGoodbyePayment(paymentFieldsFromRow(encompassLoan as Record<string, unknown>),
              firstPaymentDate, payment.due_date, payment.total_amount_due ?? null);
          }
        }
      } catch (error) {
        log.endTimer(timer, { metadata: { success: false } });
        return { success: false, error: userFacingServicingError(error, 'Existing payments require review.') };
      }
      // Operational history exists (e.g. pre-recon sprouted loan with recorded payments).
      // Update header fields but preserve payment history; trim future scheduled rows
      // that fall on or after the investor cutoff.
      log.info('Servicing loan has operational history — reconciling header + trimming future schedule', {
        bankerReconId, servicingLoanId: existingLoan.id, nonScheduledPayments: nonScheduledCount
      });

      await supabase
        .from('servicing_loans')
        .update({
          banker_recon_id: bankerReconId,
          ...(existingLoan.sync_error?.startsWith('PAYMENT_') ? { sync_error: null } : {}),
          first_payment_to_investor: firstPaymentToInvestor,
          investor_purchase_date: recon.investor_purchase_date || null,
          investor_name: purchaseInvestorName,
          investor_template_id: investorTemplateId,
          interim_servicing_data: mergeInterimServicingData(
            existingLoan.interim_servicing_data,
            investorTemplateResolution
          ),
          prepaid_interest: toNumber(encompassLoan?.fees_prepaid_interest),
          status: servicingStatus
        })
        .eq('id', existingLoan.id);

      // Trim future scheduled payments at or past the investor cutoff
      await supabase
        .from('servicing_payments')
        .delete()
        .eq('servicing_loan_id', existingLoan.id)
        .eq('status', 'scheduled')
        .gte('due_date', firstPaymentToInvestor);

      // Re-derive all aggregates from surviving payment rows
      await recalculateLoanTotals(existingLoan.id);

      const investorWirePrincipalReduction = calculateInvestorWirePrincipalReduction(
        loanAmount,
        interestRate,
        termMonths,
        firstPaymentDate,
        firstPaymentToInvestor,
        interestOnlyTermMonths
      );
      if (investorWirePrincipalReduction !== null) {
        await supabase
          .from('banker_recons')
          .update({ servicing_principal_collected: investorWirePrincipalReduction })
          .eq('id', bankerReconId);
      }

      log.endTimer(timer, { success: true, reconciledHistory: true });
      return { success: true, servicingLoanId: existingLoan.id };
    }
  }

  // 5. Upsert servicing loan (idempotent on org + encompass_loan_id)
  // Use first_payment_date as string (already resolved above)
  const { data: servicingLoan, error: upsertError } = await supabase
    .from('servicing_loans')
    .upsert(
      {
        organization_id: recon.organization_id,
        encompass_loan_id: recon.encompass_loan_id,
        banker_recon_id: bankerReconId,
        ...(existingLoan?.sync_error?.startsWith('PAYMENT_') ? { sync_error: null } : {}),
        loan_number: recon.loan_number,
        borrower_name: borrowerName,
        loan_amount: loanAmount,
        interest_rate: interestRate,
        loan_term_months: termMonths,
        closing_date: recon.closing_date || encompassLoan?.closing_date || null,
        funded_date: encompassLoan?.funded_date || null,
        first_payment_date: firstPaymentDate,
        investor_purchase_date: recon.investor_purchase_date || null,
        first_payment_to_investor: firstPaymentToInvestor,
        investor_name: purchaseInvestorName,
        prepaid_interest: toNumber(encompassLoan?.fees_prepaid_interest),
        investor_template_id: investorTemplateId,
        interim_servicing_data: mergeInterimServicingData(
          existingLoan?.interim_servicing_data,
          investorTemplateResolution
        ),
        status: servicingStatus,
        payment_count: paymentCount,
        created_by: recon.finalized_by || recon.created_by
      },
      { onConflict: 'organization_id,encompass_loan_id' }
    )
    .select()
    .single();

  if (upsertError || !servicingLoan) {
    log.error('Failed to upsert servicing loan', { bankerReconId }, upsertError);
    log.endTimer(timer, { success: false });
    // The database text stays in the log; this message reaches the finalize toast.
    return { success: false, error: 'The servicing record could not be saved. Try again.' };
  }

  // 6. Generate payment schedule
  const schedule = generatePaymentSchedule(
    loanAmount,
    interestRate,
    termMonths,
    monthlyPI,
    monthlyEscrow,
    firstPaymentDate,
    firstPaymentToInvestor
  );

  // Always delete existing scheduled payments (guard above ensures all are pristine)
  await supabase
    .from('servicing_payments')
    .delete()
    .eq('servicing_loan_id', servicingLoan.id);

  if (schedule.length > 0) {
    // Insert schedule rows
    const paymentRows = schedule.map((entry) => ({
      servicing_loan_id: servicingLoan.id,
      payment_number: entry.payment_number,
      due_date: entry.due_date,
      principal_due: entry.principal_due,
      interest_due: entry.interest_due,
      escrow_due: entry.escrow_due,
      total_amount_due: entry.total_amount_due,
      interest_rate: entry.interest_rate,
      status: 'scheduled' as const,
      source: 'manual' as const
    }));

    const { error: insertError } = await supabase
      .from('servicing_payments')
      .insert(paymentRows);

    if (insertError) {
      log.error('Failed to insert payment schedule', { servicingLoanId: servicingLoan.id }, insertError);
    } else {
      // Update totals + computed fields
      const totalExpected = schedule.reduce((sum, e) => sum + e.total_amount_due, 0);
      await supabase
        .from('servicing_loans')
        .update({
          total_expected_payments: Math.round(totalExpected * 100) / 100,
          payment_count: schedule.length,
          next_payment_due_date: schedule[0].due_date,
          received_payment_count: 0,
          has_overdue_payments: false
        })
        .eq('id', servicingLoan.id);

      // Write expected servicing amounts to banker recon
      const totalInterestDue = schedule.reduce((sum, e) => sum + e.interest_due, 0);
      const totalPrincipalDue = schedule.reduce((sum, e) => sum + e.principal_due, 0);
      const investorWirePrincipalReduction = calculateInvestorWirePrincipalReduction(
        loanAmount,
        interestRate,
        termMonths,
        firstPaymentDate,
        firstPaymentToInvestor,
        interestOnlyTermMonths
      );
      await supabase
        .from('banker_recons')
        .update({
          servicing_interest_income: Math.round(totalInterestDue * 100) / 100,
          servicing_principal_collected: investorWirePrincipalReduction ?? Math.round(totalPrincipalDue * 100) / 100
        })
        .eq('id', bankerReconId);
    }
  } else {
    // Zero-payment loan: reset all schedule-derived fields
    await supabase
      .from('servicing_loans')
      .update({
        total_expected_payments: 0,
        payment_count: 0,
        next_payment_due_date: null,
        received_payment_count: 0,
        has_overdue_payments: false
      })
      .eq('id', servicingLoan.id);

    await supabase
      .from('banker_recons')
      .update({
        servicing_interest_income: 0,
        servicing_principal_collected: 0
      })
      .eq('id', bankerReconId);
  }

  if (schedule.length > 0) {

    // Non-blocking document validation (log-only, never fail)
    try {
      const { data: amortDoc } = await supabase
        .from('encompass_attachments')
        .select('extracted_text')
        .eq('encompass_loan_id', recon.encompass_loan_id)
        .ilike('title', '%Amortization Schedule%')
        .eq('extraction_status', 'completed')
        .maybeSingle();

      if (amortDoc?.extracted_text) {
        const parsed = parseAmortizationMarkdown(amortDoc.extracted_text);
        const mismatches = validateScheduleAgainstDocument(schedule, parsed);
        if (mismatches.length > 0) {
          log.warn('Amortization schedule mismatch', { loanNumber: recon.loan_number, mismatches });
        } else if (parsed.length > 0) {
          log.info('Amortization schedule validated against document', { loanNumber: recon.loan_number, paymentsMatched: parsed.length });
        }
      }
    } catch (e) {
      log.warn('Amortization validation skipped', { error: e });
    }
  }

  log.info('Created servicing loan from banker recon', {
    bankerReconId,
    servicingLoanId: servicingLoan.id,
    loanNumber: recon.loan_number,
    paymentCount
  });
  log.endTimer(timer, { success: true });
  return { success: true, servicingLoanId: servicingLoan.id };
}

// ============================================================================
// SUMMARY METRICS (Derived from payment rows for accuracy)
// ============================================================================

/**
 * Get aggregate summary metrics for the dashboard.
 * Derives stats from servicing_payments rows directly.
 */
export async function getSummaryMetrics(
  organizationId: string
): Promise<{ metrics: ServicingSummaryMetrics; upcomingLoans: ServicingLoan[] }> {
  const timer = log.startTimer('getSummaryMetrics');

  // Get active loans
  const { data: loans } = await supabase
    .from('servicing_loans')
    .select('id, status')
    .eq('organization_id', organizationId);

  const activeLoans = (loans || []).filter((l) => l.status === 'active');
  const allLoanIds = (loans || []).map((l) => l.id);

  // Get all payments for these loans
  const { data: payments } = await supabase
    .from('servicing_payments')
    .select('total_amount_due, total_amount_received, principal_received, interest_received, status, due_date')
    .in('servicing_loan_id', allLoanIds.length > 0 ? allLoanIds : ['__none__']);

  const today = new Date().toISOString().split('T')[0];
  let totalExpected = 0;
  let totalCollected = 0;
  let overdueCount = 0;

  for (const p of payments || []) {
    totalExpected += toNumber(p.total_amount_due);
    totalCollected += toNumber(p.total_amount_received);
    if (p.status !== 'received' && p.status !== 'waived' && p.due_date < today) {
      overdueCount++;
    }
  }

  // Net income = interest only (principal reduces balance, escrow is pass-through)
  const totalInterestReceived = (payments || []).reduce(
    (sum, p) => sum + toNumber(p.interest_received),
    0
  );

  // Include virtual rows from encompass_loans in active count
  const upcomingLoans = await listUpcomingServicingLoans(organizationId);

  const metrics: ServicingSummaryMetrics = {
    totalActiveLoans: activeLoans.length + upcomingLoans.length,
    totalExpectedPayments: Math.round(totalExpected * 100) / 100,
    totalCollected: Math.round(totalCollected * 100) / 100,
    totalOutstanding: Math.round((totalExpected - totalCollected) * 100) / 100,
    netServicingIncome: Math.round(totalInterestReceived * 100) / 100,
    overduePaymentsCount: overdueCount
  };

  log.endTimer(timer, { success: true });
  return { metrics, upcomingLoans };
}

// ============================================================================
// DELETE
// ============================================================================

/**
 * Delete a servicing loan (cascades to payments)
 */
export async function deleteServicingLoan(id: string): Promise<boolean> {
  const timer = log.startTimer('deleteServicingLoan');

  const { error } = await supabase
    .from('servicing_loans')
    .delete()
    .eq('id', id);

  if (error) {
    log.error('Failed to delete servicing loan', { id }, error);
    log.endTimer(timer, { success: false });
    return false;
  }

  log.info('Deleted servicing loan', { id });
  log.endTimer(timer, { success: true });
  return true;
}

// ============================================================================
// FORM 1098 DATA
// ============================================================================

/**
 * Fetch encompass_loans fields needed for Form 1098 rendering.
 * Joins through servicing_loans.encompass_loan_id → encompass_loans.encompass_loan_id.
 */
export async function getForm1098Data(encompassLoanId: string, organizationId: string): Promise<Form1098Data | null> {
  const { data, error } = await supabase
    .from('encompass_loans')
    .select(
      'property_address, subject_property_city, subject_property_state, property_zip, ' +
      'closing_date, proposed_mi_payment, ' +
      'borrower_mailing_street, borrower_mailing_city, borrower_mailing_state, borrower_mailing_zip, ' +
      'rate_lock_investor_commitment'
    )
    .eq('encompass_loan_id', encompassLoanId)
    .maybeSingle();

  if (error) {
    log.error('Failed to fetch Form 1098 data', { encompassLoanId }, error);
    return null;
  }

  if (!data) {
    return null;
  }

  // SSN via RPC (SECURITY INVOKER preserves RLS)
  const { data: ssn4 } = await supabase.rpc('get_ssn_last4', { eid: encompassLoanId });

  // Company profile for lender info (org-scoped)
  const { data: company } = await supabase
    .from('company_profiles')
    .select('federal_ein, legal_name, company_name, headquarters_address, main_phone')
    .eq('organization_id', organizationId)
    .maybeSingle();

  const hq = company?.headquarters_address as { street?: string; suite?: string; city?: string; state?: string; zip?: string } | null;

  return {
    property_address: data.property_address ?? null,
    subject_property_city: data.subject_property_city ?? null,
    subject_property_state: data.subject_property_state ?? null,
    property_zip: data.property_zip ?? null,
    closing_date: data.closing_date ?? null,
    proposed_mi_payment: data.proposed_mi_payment ?? null,
    borrower_mailing_street: data.borrower_mailing_street ?? null,
    borrower_mailing_city: data.borrower_mailing_city ?? null,
    borrower_mailing_state: data.borrower_mailing_state ?? null,
    borrower_mailing_zip: data.borrower_mailing_zip ?? null,
    borrower_ssn_last4: ssn4 || null,
    investor_loan_number: data.rate_lock_investor_commitment ?? null,
    lender_name: company?.legal_name || company?.company_name || null,
    lender_tin: company?.federal_ein || null,
    lender_address: hq ? `${hq.street || ''}${hq.suite ? `, Suite ${hq.suite}` : ''}`.trim() : null,
    lender_city_state_zip: hq ? `${hq.city || ''}, ${hq.state || ''} ${hq.zip || ''}`.trim() : null,
    lender_phone: company?.main_phone || null
  };
}

// ============================================================================
// SERVICING FORM PDF (Print On Demand)
// ============================================================================

/**
 * Generate a servicing form PDF via the generate-servicing-form edge function.
 * Uses Encompass Print On Demand with Supabase Storage caching.
 *
 * @param encompassLoanId The Encompass loan GUID
 * @param formName Exact form name, e.g. "First Payment Letter", "Amortization Schedule"
 * @returns Signed URL + filename, or null on error
 */
export async function getServicingFormUrl(
  encompassLoanId: string,
  formName: string,
  signal?: AbortSignal
): Promise<{ url: string; fileName: string; error?: string } | null> {
  const { data, error } = await supabase.functions.invoke('generate-servicing-form', {
    body: { encompassLoanId, formName },
    ...(signal ? { signal } : {})
  });

  // supabase.functions.invoke returns error for non-2xx or network failures
  if (error) {
    const msg = error.message || 'Network error';
    log.error('Edge function invocation failed', { encompassLoanId, formName, error: msg });
    return { url: '', fileName: '', error: msg };
  }

  // Edge function always returns 200 with { success, error?, url? }
  if (!data?.success) {
    const msg = data?.error || 'Unknown error';
    log.warn('Form generation failed', { encompassLoanId, formName, serverError: msg });
    return { url: '', fileName: '', error: msg };
  }

  return { url: data.url, fileName: `${formName}.pdf` };
}

/**
 * Pre-cache servicing form PDFs in Supabase Storage.
 * Called fire-and-forget after servicing loan creation during banker recon finalization.
 * Uses Promise.allSettled so one failure doesn't block the other.
 */
export async function precacheServicingForms(encompassLoanId: string): Promise<void> {
  const forms = ['First Payment Letter', 'Amortization Schedule'];
  const results = await Promise.allSettled(
    forms.map(formName => getServicingFormUrl(encompassLoanId, formName))
  );

  for (let i = 0; i < forms.length; i++) {
    const r = results[i];
    if (r.status === 'fulfilled' && r.value && !r.value.error) {
      log.info('Pre-cached servicing form', { encompassLoanId, formName: forms[i] });
    } else {
      const reason = r.status === 'rejected' ? r.reason : r.value?.error || 'unknown';
      log.warn('Failed to pre-cache servicing form', { encompassLoanId, formName: forms[i], reason });
    }
  }
}

// ============================================================================
// SERVICING FORM CACHE CHECK
// ============================================================================

/** Must match the slugify + path pattern in generate-servicing-form edge function. */
function slugifyFormName(name: string): string {
  return name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
}

/**
 * Check whether servicing form PDFs are already cached in Supabase Storage.
 * Lightweight — no edge function call, just checks if the file exists.
 */
export async function checkServicingFormCache(
  encompassLoanId: string,
  formNames: string[]
): Promise<Record<string, boolean>> {
  const result: Record<string, boolean> = Object.fromEntries(
    formNames.map((formName) => [formName, false])
  );
  const folder = `servicing-forms/${encompassLoanId}`;
  const storage = supabase.storage.from('loan-documents');

  // Exact object checks avoid a bucket-wide RLS scan in Storage's folder search.
  // storage-js may reject (rather than return an error) for failed HEAD requests.
  const checks = await Promise.allSettled(
    formNames.map(async (formName) => {
      const { data, error } = await storage.exists(`${folder}/${slugifyFormName(formName)}.pdf`);
      return !error && data === true;
    })
  );

  for (const [index, check] of checks.entries()) {
    if (check.status === 'fulfilled') {
      result[formNames[index]] = check.value;
    } else {
      log.warn('Unable to confirm cached servicing form', { encompassLoanId, formName: formNames[index] });
    }
  }

  return result;
}

// ============================================================================
// SERVICING TRANSFER NOTICE DATA
// ============================================================================

/**
 * Fetch new servicer contact info from investor_encompass_template_contacts.
 * Returns separate Payment (mailing address) and CustomerService (inquiry) contacts.
 */
export async function getNewServicerContact(
  investorTemplateId?: string | null,
  bankerReconId?: string | null
): Promise<{ payment: ServicerContact | null; customerService: ServicerContact | null }> {
  const result = { payment: null as ServicerContact | null, customerService: null as ServicerContact | null };

  // Resolve template_id. A stored servicing_loans.investor_template_id is useful
  // lineage, but product-scoped templates can change; recon context wins when it
  // can deterministically resolve the current product assignment.
  let templateId = investorTemplateId;

  if (bankerReconId) {
    const { data: recon } = await supabase
      .from('banker_recons')
      .select('investor_id, organization_id, encompass_loan_id')
      .eq('id', bankerReconId)
      .maybeSingle();

    if (recon?.investor_id && recon.organization_id) {
      const { data: el } = await supabase
        .from('encompass_loans')
        .select('rate_lock_loan_program, trans_details_loan_type, cx_doc_type')
        .eq('encompass_loan_id', recon.encompass_loan_id)
        .maybeSingle();

      const resolved = await resolveInvestorTemplateForLoan(
        recon.investor_id,
        recon.organization_id,
        el ?? {}
      );
      if (!templateId || (resolved.templateId && resolved.templateId !== templateId)) {
        templateId = resolved.templateId;
      }
    }
  }

  if (!templateId) {
    return result;
  }

  const { data: contacts, error } = await supabase
    .from('investor_encompass_template_contacts')
    .select('contact_type, investor_name, contact_name, department, attention_to, address_street1, address_street2, city, state, zip, phone, fax, email, website, notes')
    .eq('template_id', templateId)
    .in('contact_type', ['Payment', 'CustomerService']);

  if (error || !contacts) {
    log.error('Failed to fetch servicer contacts', { templateId }, error);
    return result;
  }

  for (const c of contacts) {
    const contact: ServicerContact = {
      name: c.investor_name || c.contact_name || '',
      department: c.department || undefined,
      attentionTo: c.attention_to || undefined,
      address: c.address_street1 || '',
      address2: c.address_street2 || undefined,
      city: c.city || '',
      state: c.state || '',
      zip: c.zip || '',
      phone: c.phone || undefined,
      email: c.email || undefined,
      website: c.website || undefined
    };

    if (c.contact_type === 'Payment') {
      result.payment = contact;
    } else if (c.contact_type === 'CustomerService') {
      result.customerService = contact;
    }
  }

  return result;
}

/**
 * Assemble all data needed for the Servicing Transfer Notice PDF.
 * Joins servicing_loans, encompass_loans, company_profiles, and investor template contacts.
 */
export async function getServicingTransferData(
  servicingLoanId: string,
  overrides?: {
    transferDate?: string;
    coupon1DueDate?: string;
    coupon2DueDate?: string;
    newServicer?: Partial<ServicerContact>;
  }
): Promise<ServicingTransferNoticeData | null> {
  const timer = log.startTimer('getServicingTransferData');

  // 1. Load servicing loan
  const loan = await getServicingLoan(servicingLoanId);
  if (!loan) {
    log.error('Servicing loan not found', { servicingLoanId });
    log.endTimer(timer, { success: false });
    return null;
  }

  // 2. Load encompass_loans data for borrower/property/payment details
  const { data: rawEl } = await supabase
    .from('encompass_loans')
    .select(
      `borrower_first_name, borrower_last_name, co_borrower_first_name, co_borrower_last_name, ` +
      `borrower_mailing_street, borrower_mailing_city, borrower_mailing_state, borrower_mailing_zip, ` +
      `property_address, subject_property_city, subject_property_state, property_zip, ` +
      `rate_lock_investor_commitment, ${GOODBYE_PAYMENT_SELECT}`
    )
    .eq('encompass_loan_id', loan.encompass_loan_id)
    .maybeSingle();

  const el = rawEl as unknown as ServicingEncompassRow | null;

  // 3. Load company profile for current servicer info
  const { data: company } = await supabase
    .from('company_profiles')
    .select('company_name, legal_name, headquarters_address, main_phone, toll_free_phone, support_email, main_email, website, company_nmls_id, logo_url')
    .limit(1)
    .single();

  // 4. Resolve new servicer from investor template contacts
  const servicerContacts = await getNewServicerContact(
    loan.investor_template_id,
    loan.banker_recon_id
  );

  // Build new servicer — prefer Payment contact for address, CustomerService for phone/inquiries
  const paymentContact = servicerContacts.payment;
  const csContact = servicerContacts.customerService;

  const newServicer: ServicerContact = {
    name: paymentContact?.name || csContact?.name || loan.investor_name || 'Unknown Servicer',
    department: paymentContact?.department || undefined,
    attentionTo: paymentContact?.attentionTo || 'Payment Processing',
    address: paymentContact?.address || csContact?.address || '',
    address2: paymentContact?.address2 || undefined,
    city: paymentContact?.city || csContact?.city || '',
    state: paymentContact?.state || csContact?.state || '',
    zip: paymentContact?.zip || csContact?.zip || '',
    phone: csContact?.phone || paymentContact?.phone || undefined,
    email: csContact?.email || paymentContact?.email || undefined,
    website: csContact?.website || paymentContact?.website || undefined,
    // Apply any user overrides
    ...overrides?.newServicer
  };

  // Build current servicer from company profile
  const hq = company?.headquarters_address as { street?: string; suite?: string; city?: string; state?: string; zip?: string } | null;
  const currentServicer: ServicerContact = {
    name: company?.legal_name || company?.company_name || 'Lendwise Mortgage, Inc.',
    address: hq ? `${hq.street || ''}${hq.suite ? `, Suite ${hq.suite}` : ''}`.trim() : '',
    city: hq?.city || '',
    state: hq?.state || '',
    zip: hq?.zip || '',
    phone: company?.main_phone || undefined,
    tollFreePhone: company?.toll_free_phone || undefined,
    email: company?.support_email || company?.main_email || undefined,
    website: company?.website || undefined
  };

  // Build borrower info
  const borrowerName = loan.borrower_name
    || (el ? `${el.borrower_first_name || ''} ${el.borrower_last_name || ''}`.trim() : 'Borrower');
  const coBorrowerName = el?.co_borrower_first_name
    ? `${el.co_borrower_first_name} ${el.co_borrower_last_name || ''}`.trim()
    : null;

  const borrowerMailingAddress = el?.borrower_mailing_street
    ? [
        el.borrower_mailing_street,
        `${el.borrower_mailing_city || ''}, ${el.borrower_mailing_state || ''} ${el.borrower_mailing_zip || ''}`.trim()
      ]
    : [];

  const propertyAddress = el?.property_address
    ? [
        el.property_address,
        `${el.subject_property_city || ''}, ${el.subject_property_state || ''} ${el.property_zip || ''}`.trim()
      ]
    : [];

  // Derive dates
  const today = new Date().toISOString().split('T')[0];
  const transferDate = overrides?.transferDate || loan.servicing_transfer_date || loan.investor_purchase_date || '';

  // Last payment to Lendwise: last servicing payment before transfer
  const payments = await getPaymentsForLoan(servicingLoanId);
  const lastLendwisePayment = payments.length > 0 ? payments[payments.length - 1] : null;
  const coupon1DueDate = overrides?.coupon1DueDate || lastLendwisePayment?.due_date || loan.first_payment_date;
  const coupon2DueDate = overrides?.coupon2DueDate || loan.first_payment_to_investor || '';

  const payment = resolveGoodbyePayment(paymentFieldsFromRow(el ?? {}), loan.first_payment_date, coupon1DueDate,
    lastLendwisePayment && !isSettledPaymentStatus(lastLendwisePayment.status) ? lastLendwisePayment.total_amount_due ?? null : undefined);
  resolveGoodbyePayment(paymentFieldsFromRow(el ?? {}), loan.first_payment_date, coupon2DueDate);

  const data: ServicingTransferNoticeData = {
    loanNumber: loan.loan_number,
    investorLoanNumber: el?.rate_lock_investor_commitment ?? null,
    borrowerName,
    coBorrowerName,
    borrowerMailingAddress,
    propertyAddress,
    currentServicer,
    newServicer,
    noticeDate: today,
    loanSoldDate: loan.investor_purchase_date,
    transferEffectiveDate: transferDate,
    lastPaymentDateCurrentServicer: coupon1DueDate,
    firstPaymentDateNewServicer: coupon2DueDate,
    principalAndInterest: payment.principalAndInterestCents / 100,
    escrowTotal: payment.collectedEscrowCents / 100,
    escrowBreakdown: null,
    totalMonthlyPayment: payment.amountCents / 100,
    hasEscrow: payment.collectedEscrowCents > 0,
    coupon1DueDate,
    coupon2DueDate
  };

  log.info('Assembled servicing transfer data', {
    loanNumber: loan.loan_number,
    newServicerName: newServicer.name,
    transferDate
  });
  log.endTimer(timer, { success: true });
  return data;
}

// ============================================================================
// PAYMENT REMINDERS
// ============================================================================

/**
 * Find loans with qualifying payments for reminder emails.
 * Returns the earliest unsettled payment for each active servicing loan.
 * This intentionally matches the single-loan reminder flow so bulk and
 * individual sends share the same eligibility rules.
 */
export async function getPaymentReminderCandidates(
  organizationId: string
): Promise<PaymentReminderLoan[]> {
  const timer = log.startTimer('getPaymentReminderCandidates');

  const todayISO = getBusinessDateString();

  // Single query: payments joined with their parent loan (inner join).
  // Far-future payments are excluded — servicing records exist from funding
  // day, but borrowers are only reminded inside the shared reminder window.
  const { data, error } = await supabase
    .from('servicing_payments')
    .select(`
      servicing_loan_id,
      payment_number,
      due_date,
      total_amount_due,
      status,
      servicing_loans!inner(
        id,
        encompass_loan_id,
        loan_number,
        borrower_name,
        organization_id,
        status
      )
    `)
    .eq('servicing_loans.organization_id', organizationId)
    .eq('servicing_loans.status', 'active')
    .not('status', 'in', '("received","waived")')
    .lte('due_date', reminderWindowCutoff(todayISO))
    .order('due_date', { ascending: true });

  if (error) {
    log.error('Failed to fetch payment reminder candidates', { error: error.message });
    log.endTimer(timer, { success: false });
    return [];
  }

  if (!data || data.length === 0) {
    log.info('No payment reminder candidates found');
    log.endTimer(timer, { success: true, count: 0 });
    return [];
  }

  // Deduplicate: keep earliest qualifying payment per loan
  const seen = new Map<string, PaymentReminderLoan>();

  for (const row of data) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const sl = (row as any).servicing_loans;
    const loanId = sl?.id as string;
    if (!loanId || seen.has(loanId)) {
      continue;
    }

    const dueDate = row.due_date as string;
    const isOverdue = dueDate < todayISO;

    const daysOverdue: number | undefined = isOverdue
      ? diffCalendarDays(dueDate, todayISO)
      : undefined;

    seen.set(loanId, {
      encompassLoanId: sl.encompass_loan_id,
      servicingLoanId: loanId,
      loanNumber: sl.loan_number,
      borrowerName: sl.borrower_name || 'Unknown',
      amountDue: toNumber(row.total_amount_due),
      dueDate,
      paymentStatus: isOverdue ? 'overdue' : 'upcoming',
      daysOverdue
    });
  }

  const candidates = [...seen.values()];
  log.info('Payment reminder candidates found', {
    total: candidates.length,
    overdue: candidates.filter(c => c.paymentStatus === 'overdue').length,
    upcoming: candidates.filter(c => c.paymentStatus === 'upcoming').length
  });
  log.endTimer(timer, { success: true, count: candidates.length });
  return candidates;
}

/**
 * Send payment reminder emails to borrowers via the edge function.
 */
export async function sendPaymentReminders(
  loans: PaymentReminderLoan[]
): Promise<PaymentReminderResult> {
  const timer = log.startTimer('sendPaymentReminders');

  if (loans.length === 0) {
    log.warn('sendPaymentReminders called with empty array');
    log.endTimer(timer, { success: true, skipped: true });
    return { success: true, summary: { sent: 0, failed: 0, skipped: 0, total: 0 }, results: [] };
  }

  try {
    const { data: session } = await supabase.auth.getSession();
    if (!session?.session?.access_token) {
      log.error('No active session for sending payment reminders');
      return { success: false, error: 'Authentication required' };
    }

    const { data, error } = await supabase.functions.invoke(
      'send-servicing-payment-reminder',
      {
        body: { loans },
        headers: {
          Authorization: `Bearer ${session.session.access_token}`
        }
      }
    );

    if (error) {
      log.error('Edge function invocation failed', { error: error.message });
      log.endTimer(timer, { success: false });
      return { success: false, error: error.message };
    }

    const result = data as PaymentReminderResult;
    log.info('Payment reminders sent', { summary: result.summary });
    log.endTimer(timer, { success: true, ...result.summary });
    return result;
  } catch (err) {
    const message = err instanceof Error ? err.message : 'Unknown error';
    log.error('Failed to send payment reminders', { error: message });
    log.endTimer(timer, { success: false });
    return { success: false, error: message };
  }
}

/**
 * Send a payment received confirmation email to the borrower after recording a payment.
 * Fire-and-forget — callers should not block on this.
 */
export async function sendPaymentReceivedConfirmation(
  payload: PaymentReceivedPayload
): Promise<PaymentReceivedResult> {
  const timer = log.startTimer('sendPaymentReceivedConfirmation');

  try {
    const { data: session } = await supabase.auth.getSession();
    if (!session?.session?.access_token) {
      log.error('No active session for sending payment received confirmation');
      return { success: false, error: 'Authentication required' };
    }

    const { data, error } = await supabase.functions.invoke(
      'send-servicing-payment-received',
      {
        body: payload,
        headers: {
          Authorization: `Bearer ${session.session.access_token}`
        }
      }
    );

    if (error) {
      log.error('Edge function invocation failed', { error: error.message });
      log.endTimer(timer, { success: false });
      return { success: false, error: error.message };
    }

    const result = data as PaymentReceivedResult;
    log.info('Payment received confirmation sent', {
      loanNumber: payload.loanNumber,
      paymentNumber: payload.paymentNumber,
      emailId: result.emailId,
      skipped: result.skipped
    });
    log.endTimer(timer, { success: true });
    return result;
  } catch (err) {
    const message = err instanceof Error ? err.message : 'Unknown error';
    log.error('Failed to send payment received confirmation', { error: message });
    log.endTimer(timer, { success: false });
    return { success: false, error: message };
  }
}
